import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Button} from '@edna/components';
import {Stack} from '@edna/components/primitives';

import {Filters, FiltersButton, PageDialog, ScrollUpButton, SearchInput} from 'src/components';
import {
  ListActions,
  ListActionsItem,
  ListActionsMain,
  PAGE_SIZE,
  Page,
  VirtualPage,
  useList,
  useVirtualStorage,
} from 'src/components/List';
import {Text} from 'src/components/primitives';
import {useToggle} from 'src/hooks';
import {EmptyImage} from 'src/images';
import PATHS from 'src/paths';

import {FiltersContent} from './FiltersContent';
import {showRegistrationModal} from './RegistrationModal';
import {useGetMessageMatcherPresetsQuery} from './api';
import {COLUMNS_COUNT, LIST_CONFIG, TFilters, TMessageMatcherPreset} from './definitions';
import * as S from './style';
import {splitListToColumns} from './utils';

const Item = React.memo<{item: TMessageMatcherPreset}>(({item}) => (
  <S.Item onClick={() => showRegistrationModal(item.id)}>
    <S.MessagePreview
      channelType="WHATSAPP"
      contentType={item.contentType}
      language={item.language}
      content={item.content}
      type="OPERATOR"
      noActions
    />
    <S.Name>{item.name}</S.Name>
  </S.Item>
));

Item.displayName = 'Item';

const PendingNode = React.memo(() => (
  <S.SkeletonList>
    {Array.from({length: COLUMNS_COUNT}).map((column, columnIndex) => (
      <Stack size="5" key={columnIndex}>
        {Array.from({length: Math.floor(PAGE_SIZE / COLUMNS_COUNT)}).map((_, index) => (
          <S.SkeletonItem key={index} />
        ))}
      </Stack>
    ))}
  </S.SkeletonList>
));

PendingNode.displayName = 'PendingNode';

export const MessageMatcherPresets = React.memo(() => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const [
    {pages, data, loadingState},
    {filters, setFilters, activeFiltersCount, hasFilters, resetFilters},
  ] = useList<TMessageMatcherPreset, TFilters>(useGetMessageMatcherPresetsQuery, LIST_CONFIG);

  const hasContent = data && data?.content?.length > 0;

  const [isFilterOpen, toggleFilter] = useToggle();

  const handleSearch = React.useCallback(
    (query: string) => setFilters((prevState) => ({...prevState, filter: query})),
    [setFilters],
  );

  const notFound = React.useMemo(
    () => (
      <PageDialog
        title={t('MessageMatcherPresets:notFound.title')}
        subtitle={t('MessageMatcherPresets:notFound.subtitle')}
        text={t('MessageMatcherPresets:notFound.hint')}
        actions={
          <Button
            size="L"
            onClick={() => navigate(PATHS.MESSAGE_MATCHERS_WHATSAPP_NEW)}
            appearance="primary"
            data-selector="MessageMatcherPresets:notFound.action"
          >
            {t('MessageMatcherPresets:notFound.action')}
          </Button>
        }
        imageComponent={EmptyImage}
      />
    ),
    [navigate, t],
  );

  const [presets, renderPresets] = useVirtualStorage(pages[0]);

  const presetsList = React.useMemo<TMessageMatcherPreset[][]>(
    () => splitListToColumns(presets),
    [presets],
  );

  return (
    <>
      <Text size="XL" bottomIndent="4" topIndent="4">
        {t('MessageMatcherPresets:title')}
      </Text>
      <Text secondary>{t('MessageMatcherPresets:subtitle')}</Text>
      <ListActions state={loadingState}>
        <ListActionsItem flex={0}>
          <FiltersButton activeFiltersCount={activeFiltersCount} onToggleFilters={toggleFilter} />
        </ListActionsItem>
        <ListActionsMain>
          <SearchInput
            onChange={handleSearch}
            minQueryLength={3}
            placeholder={t('MessageMatcherPresets:searchPlaceholder')}
            query={filters.filter}
          />
        </ListActionsMain>
      </ListActions>
      {isFilterOpen && (
        <Filters
          activeFiltersCount={activeFiltersCount}
          filters={filters}
          resetFilters={resetFilters}
          setFilters={setFilters}
          toggleFilter={toggleFilter}
        >
          <FiltersContent />
        </Filters>
      )}
      {(hasContent || hasFilters) && (
        <S.ListTotal>
          {t(`MessageMatcherPresets:${hasFilters ? 'totalSearch' : 'totalCount'}`, {
            count: data?.totalElements ?? 0,
          })}
        </S.ListTotal>
      )}
      {hasContent && <ScrollUpButton />}
      <Page
        useQuery={useGetMessageMatcherPresetsQuery}
        payload={pages[0]}
        notFoundNode={notFound}
        hasFilters={hasFilters}
        pendingNode={<PendingNode />}
        renderItems={() => (
          <>
            {pages.map((payload, index) => (
              <VirtualPage
                key={index}
                useQuery={useGetMessageMatcherPresetsQuery}
                payload={payload}
                index={index}
                renderItems={renderPresets}
              />
            ))}
            <S.Row>
              {presetsList.map((presetsColumn, index) => (
                <Stack size="5" key={index}>
                  {presetsColumn.map((preset) => (
                    <Item key={preset.id} item={preset} />
                  ))}
                </Stack>
              ))}
            </S.Row>
          </>
        )}
      />
    </>
  );
});

MessageMatcherPresets.displayName = 'MessageMatcherPresets';
